import type { CaseFilter } from "@/queries/useFetchCaseFiltersets.ts";
import {
    DndContext,
    type DragEndEvent,
    KeyboardSensor,
    PointerSensor,
    closestCenter,
    useSensor,
    useSensors,
} from "@dnd-kit/core";
import { restrictToVerticalAxis } from "@dnd-kit/modifiers";
import { arrayMove, sortableKeyboardCoordinates } from "@dnd-kit/sortable";
import { Box, Button, CircularProgress, Modal, Typography } from "@mui/material";
import { styled } from "@mui/material/styles";
import type React from "react";
import { useEffect, useMemo, useState } from "react";
import AvailableFields from "./AvailableFields";
import SelectedFields from "./SelectedFields";

const ModalContent = styled(Box)(({ theme }) => ({
    position: "absolute",
    top: "50%",
    left: "50%",
    transform: "translate(-50%, -50%)",
    width: "90%",
    maxWidth: 800,
    maxHeight: "90vh",
    backgroundColor: theme.palette.background.paper,
    borderRadius: theme.shape.borderRadius,
    boxShadow: theme.shadows[5],
    display: "flex",
    flexDirection: "column",
}));

const ContentArea = styled(Box)(({ theme }) => ({
    padding: theme.spacing(4),
    overflow: "auto",
    maxHeight: "calc(90vh - 140px)",
}));

const ColumnsContainer = styled(Box)(({ theme }) => ({
    display: "grid",
    gridTemplateColumns: "repeat(2, 1fr)",
    gap: theme.spacing(4),
    marginTop: theme.spacing(3),
}));

const ModalFooter = styled(Box)(({ theme }) => ({
    borderTop: `1px solid ${theme.palette.divider}`,
    padding: theme.spacing(2, 4),
    display: "flex",
    justifyContent: "space-between",
    backgroundColor: theme.palette.background.paper,
}));

export interface AvailableField {
    key: string;
    label: string;
}

export const AVAILABLE_FIELDS: AvailableField[] = [
    { key: "id", label: "Case ID" },
    { key: "clientName", label: "Client Name" },
    { key: "phoneNumber", label: "Client Phone Number" },
    { key: "emailAddress", label: "Client Email" },
    { key: "metaCreatedWhen", label: "Case Created DateTime" },
    { key: "metaCreatedByPretty", label: "Case Created By" },
    { key: "caseType", label: "Type" },
    { key: "anteLitemState", label: "Incident State / County" },
    { key: "dateOfIncident", label: "DOI" },
    { key: "daysToSol", label: "SOL Date" },
    { key: "anteLitemYesNo", label: "Ante Litem" },
    { key: "anteLitemGovernmentEntity", label: "Government Entity" },
    { key: "anteLitem", label: "Ante Litem Sent On" },
    { key: "policyLimits", label: "Policy Limits" },
    { key: "liabilityDeclarationPage", label: "Liability Declaration Page" },
    { key: "idCaseStatusUM", label: "UM/UIM Case Status" },
    { key: "medpayAmount", label: "Med Pay Amount" },
    { key: "erisaUMSentOn", label: "ERISA Date" },
    { key: "assignedTeam", label: "Team" },
    { key: "assignedToAttorney", label: "Attorney" },
    { key: "assignedToDisbursementSpecialist", label: "Disbursement Specialist" },
    { key: "anteLitemEntity", label: "AL Entity" },
    { key: "caseStatus", label: "Liability Status" },
    { key: "liabilityAccepted", label: "Liability Accepted" },
    { key: "lbtyDemandSentFrom", label: "Liability Demand Sent On" },
    { key: "uMUIMAccepted", label: "UM/UIM Accepted" },
    { key: "demandUMSentOn", label: "UM/UIM Demand Sent On" },
    { key: "medpay", label: "Med Pay" },
    { key: "assignedToIntakeSpecialist", label: "Intake Specialist" },
    { key: "assignedToParalegal", label: "Case Manager" },
    { key: "financeLoan", label: "Finance Loan" },
    { key: "attorneyFee", label: "Attorney %" },
    { key: "crmStatus", label: "Lead Status" },
    { key: "litigationSubStatus", label: "Substatus" },
    { key: "referralRadio", label: "Type of Referral" },
    { key: "idReferredByCompany", label: "Referred by Company" },
    { key: "idReferredBy", label: "Referred By" },
    { key: "companion1CaseNumber", label: "Companion 1 Case Number" },
    { key: "companion1ClientName", label: "Companion 1 Client Name" },
    { key: "companion2CaseNumber", label: "Companion 2 Case Number" },
    { key: "companion2ClientName", label: "Companion 2 Client Name" },
    { key: "companion3CaseNumber", label: "Companion 3 Case Number" },
    { key: "companion3ClientName", label: "Companion 3 Client Name" },
    { key: "companion4CaseNumber", label: "Companion 4 Case Number" },
    { key: "companion4ClientName", label: "Companion 4 Client Name" },
    { key: "companion5CaseNumber", label: "Companion 5 Case Number" },
    { key: "companion5ClientName", label: "Companion 5 Client Name" },
    { key: "umUimClaimFields", label: "UM/UIM CLAIM Fields" },
    { key: "liabilityClaimFields", label: "Liability CLAIM Fields" },
];

interface ExportModalProps {
    open: boolean;
    onClose: () => void;
    onExport: (fields: AvailableField[]) => void;
    activeFilter: CaseFilter | null;
}

const ExportModal: React.FC<ExportModalProps> = ({ open, onClose, onExport, activeFilter }) => {
    const [availableFields, setAvailableFields] = useState<AvailableField[]>(AVAILABLE_FIELDS);
    const [selectedFields, setSelectedFields] = useState<AvailableField[]>([]);
    const [searchTerm, setSearchTerm] = useState("");
    const [loading, setLoading] = useState(false);

    const sensors = useSensors(
        useSensor(PointerSensor),
        useSensor(KeyboardSensor, {
            coordinateGetter: sortableKeyboardCoordinates,
        }),
    );

    const filteredAvailableFields = useMemo(() => {
        return availableFields.filter((field) =>
            field.label.toLowerCase().includes(searchTerm.toLowerCase()),
        );
    }, [availableFields, searchTerm]);

    const handleDragEnd = (event: DragEndEvent) => {
        const { active, over } = event;

        if (!over) {
            return;
        }

        const activeId = active.id as string;
        const overId = over.id as string;

        const activeContainer = active.data.current?.sortable.containerId;
        const overContainer = over.data.current?.sortable.containerId;

        if (activeContainer !== overContainer) {
            const overIndex =
                overContainer === "available"
                    ? availableFields.findIndex((field) => field.key === overId)
                    : selectedFields.findIndex((field) => field.key === overId);

            if (activeContainer === "available") {
                const activeField = availableFields.find((field) => field.key === activeId);
                if (!activeField) {
                    return;
                }

                setAvailableFields((fields) => fields.filter((field) => field.key !== activeId));
                setSelectedFields((fields) => {
                    const newFields = [...fields];
                    newFields.splice(overIndex, 0, activeField);
                    return newFields;
                });
            } else {
                const activeField = selectedFields.find((field) => field.key === activeId);
                if (!activeField) {
                    return;
                }

                setSelectedFields((fields) => fields.filter((field) => field.key !== activeId));
                setAvailableFields((fields) => {
                    const newFields = [...fields];
                    newFields.splice(overIndex, 0, activeField);
                    return newFields;
                });
            }
        } else {
            const items = activeContainer === "available" ? availableFields : selectedFields;
            const activeIndex = items.findIndex((field) => field.key === activeId);
            const overIndex = items.findIndex((field) => field.key === overId);

            if (activeIndex !== overIndex) {
                const newItems = arrayMove(items, activeIndex, overIndex);
                if (activeContainer === "available") {
                    setAvailableFields(newItems);
                } else {
                    setSelectedFields(newItems);
                }
            }
        }
    };

    const handleQuickAdd = (fieldKey: string) => {
        const fieldToAdd = availableFields.find((f) => f.key === fieldKey);
        if (!fieldToAdd) {
            return;
        }

        setAvailableFields((fields) => fields.filter((f) => f.key !== fieldKey));
        setSelectedFields((fields) => [...fields, fieldToAdd]);
    };

    const handleRemoveField = (fieldKey: string) => {
        const fieldToRemove = selectedFields.find((f) => f.key === fieldKey);
        if (!fieldToRemove) {
            return;
        }

        setSelectedFields((fields) => fields.filter((f) => f.key !== fieldKey));
        setAvailableFields((fields) => [...fields, fieldToRemove]);
    };

    const handleAddAll = () => {
        setSelectedFields((prev) => [...prev, ...filteredAvailableFields]);
        setAvailableFields((fields) =>
            fields.filter((field) => !filteredAvailableFields.some((f) => f.key === field.key)),
        );
    };

    const handleClearAll = () => {
        const fieldsToMove = selectedFields;
        setAvailableFields((prev) => [...prev, ...fieldsToMove]);
        setSelectedFields([]);
    };

    const handleExport = () => {
        setLoading(true);
        onExport(selectedFields);
    };

    useEffect(() => {
        if (activeFilter?.exportFields) {
            const newSelectedFields = JSON.parse(activeFilter.exportFields) as AvailableField[];
            setSelectedFields(newSelectedFields);
            setAvailableFields(
                AVAILABLE_FIELDS.filter(
                    (field) => !newSelectedFields.some((f) => f.key === field.key),
                ),
            );
        }
    }, [activeFilter]);

    useEffect(() => {
        setLoading(false);
    }, [open]);

    return (
        <Modal open={open} onClose={onClose}>
            <ModalContent>
                <ContentArea>
                    <Typography variant="h5" component="h2" gutterBottom>
                        Configure Export Fields
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                        Drag fields between columns to select which data to include in your export.
                        Reorder selected fields to determine their order in the CSV file.
                    </Typography>

                    <DndContext
                        sensors={sensors}
                        collisionDetection={closestCenter}
                        onDragEnd={handleDragEnd}
                        modifiers={[restrictToVerticalAxis]}
                    >
                        <ColumnsContainer>
                            <AvailableFields
                                fields={filteredAvailableFields}
                                searchTerm={searchTerm}
                                onSearchChange={setSearchTerm}
                                onQuickAdd={handleQuickAdd}
                                onAddAll={handleAddAll}
                            />
                            <SelectedFields
                                fields={selectedFields}
                                onRemove={handleRemoveField}
                                onClearAll={handleClearAll}
                            />
                        </ColumnsContainer>
                    </DndContext>
                </ContentArea>

                <ModalFooter>
                    <Button onClick={onClose} color="inherit">
                        Cancel
                    </Button>
                    <Button
                        onClick={handleExport}
                        variant="contained"
                        color="primary"
                        disabled={selectedFields.length === 0 || loading}
                        sx={{ minWidth: "140px" }}
                    >
                        {loading ? <CircularProgress size={"12px"} /> : "Download CSV"}
                    </Button>
                </ModalFooter>
            </ModalContent>
        </Modal>
    );
};

export default ExportModal;
